import i18next from 'i18next';
import { AppLanguage, appLanguageOf } from 'config/languages';

/**
 * What each market is called on screen, and in what order the markets are
 * listed.
 *
 * The feed names markets its own way ("Full Time Result", "Alternative Goal
 * Line", "Last Team to Score") and hands them to us in a priority order that
 * differs from match to match, which is why the same league read differently
 * every time you opened it. This catalogue gives every market we recognise one
 * name in the wording players here are used to, and one fixed position in the
 * list; markets outside it keep their translated feed name and follow after,
 * in the feed's own priority order.
 *
 * Two feed markets may share a label on purpose: the main and the alternative
 * lines of the same bet are two rows, both called "Over/Under", sitting next
 * to each other.
 *
 * The feed name is what settlement matches on (see the API's validators), so
 * it is never rewritten - only what is shown is.
 */

type Labels = Record<AppLanguage, string>;

type CatalogEntry = {
  /** Feed market names this row covers, as the provider spells them. */
  feed: string[];
  /** What to call it, in each language the app is offered in. */
  labels: Labels;
};

/** In the order the list should read, top to bottom. */
const CATALOG: CatalogEntry[] = [
  // Soccer reads in the order of the reference book the owner supplied
  // (2026-09-28 screenshots): result, handicaps, goal lines, team totals, draw
  // no bet, first/last to score, both teams to score, halves, clean sheet,
  // exact goals and ranges, odd/even, HT/FT, correct scores, scorers, combos,
  // then specials and corners. Markets it does not show (Asian lines) sit next
  // to their closest relatives.
  {
    feed: ['Full Time Result', 'Match Result', 'To Win Match'],
    labels: {
      en: '3-way',
      de: '1X2 – Endergebnis',
      sr: '1X2 – konačan ishod',
      hr: '1X2 – konačan ishod',
      bs: '1X2 – konačan ishod',
      it: '1X2 – esito finale',
      fr: '1X2 – résultat final',
      sq: '1X2 – rezultati final',
      tr: '1X2 – Maç Sonucu',
      es: '1X2 – Resultado final',
    },
  },
  {
    feed: ['Double Chance'],
    labels: {
      en: 'Double chance',
      de: 'Doppelte Chance',
      sr: 'Dvostruka šansa',
      hr: 'Dvostruka šansa',
      bs: 'Dvostruka šansa',
      it: 'Doppia chance',
      fr: 'Double chance',
      sq: 'Shans i dyfishtë',
      tr: 'Çifte Şans',
      es: 'Doble oportunidad',
    },
  },
  {
    feed: ['Handicap Result', 'Handicap'],
    labels: {
      en: 'Handicap',
      de: 'Handicap',
      sr: 'Hendikep',
      hr: 'Hendikep',
      bs: 'Hendikep',
      it: 'Handicap',
      fr: 'Handicap',
      sq: 'Hendikep',
      tr: 'Handikap',
      es: 'Hándicap',
    },
  },
  {
    feed: ['Alternative Handicap Result'],
    labels: {
      en: 'Handicap',
      de: 'Handicap',
      sr: 'Hendikep',
      hr: 'Hendikep',
      bs: 'Hendikep',
      it: 'Handicap',
      fr: 'Handicap',
      sq: 'Hendikep',
      tr: 'Handikap',
      es: 'Hándicap',
    },
  },
  {
    feed: ['1st Half Handicap Result', '1st Half Handicap'],
    labels: {
      en: '1st Half - Handicap',
      de: '1. Halbzeit – Handicap',
      sr: '1. poluvreme – hendikep',
      hr: '1. poluvrijeme – hendikep',
      bs: '1. poluvrijeme – hendikep',
      it: '1° tempo – handicap',
      fr: '1re mi-temps – handicap',
      sq: 'Pjesa e parë – hendikep',
      tr: '1. Yarı – Handikap',
      es: '1.ª parte – hándicap',
    },
  },
  {
    feed: ['Alternative 1st Half Handicap Result'],
    labels: {
      en: '1st Half - Handicap',
      de: '1. Halbzeit – Handicap',
      sr: '1. poluvreme – hendikep',
      hr: '1. poluvrijeme – hendikep',
      bs: '1. poluvrijeme – hendikep',
      it: '1° tempo – handicap',
      fr: '1re mi-temps – handicap',
      sq: 'Pjesa e parë – hendikep',
      tr: '1. Yarı – Handikap',
      es: '1.ª parte – hándicap',
    },
  },
  {
    feed: ['Handicap 2-Way', 'Match Handicap'],
    labels: {
      en: 'Handicap 2-way',
      de: 'Handicap 2-Weg',
      sr: 'Hendikep 2 ishoda',
      hr: 'Hendikep 2 ishoda',
      bs: 'Hendikep 2 ishoda',
      it: 'Handicap 2 esiti',
      fr: 'Handicap 2 issues',
      sq: 'Hendikep 2 mundësi',
      tr: 'Handikap 2 seçenekli',
      es: 'Hándicap 2 opciones',
    },
  },
  {
    feed: ['Asian Handicap'],
    labels: {
      en: 'Asian Handicap',
      de: 'Asiatisches Handicap',
      sr: 'Azijski hendikep',
      hr: 'Azijski hendikep',
      bs: 'Azijski hendikep',
      it: 'Handicap asiatico',
      fr: 'Handicap asiatique',
      sq: 'Hendikep aziatik',
      tr: 'Asya Handikapı',
      es: 'Hándicap asiático',
    },
  },
  {
    feed: ['Alternative Asian Handicap'],
    labels: {
      en: 'Asian Handicap',
      de: 'Asiatisches Handicap',
      sr: 'Azijski hendikep',
      hr: 'Azijski hendikep',
      bs: 'Azijski hendikep',
      it: 'Handicap asiatico',
      fr: 'Handicap asiatique',
      sq: 'Hendikep aziatik',
      tr: 'Asya Handikapı',
      es: 'Hándicap asiático',
    },
  },
  {
    feed: ['1st Half Handicap 2-Way'],
    labels: {
      en: '1st Half - Handicap 2-way',
      de: '1. Halbzeit – Handicap 2-Weg',
      sr: '1. poluvreme – hendikep 2 ishoda',
      hr: '1. poluvrijeme – hendikep 2 ishoda',
      bs: '1. poluvrijeme – hendikep 2 ishoda',
      it: '1° tempo – handicap 2 esiti',
      fr: '1re mi-temps – handicap 2 issues',
      sq: 'Pjesa e parë – hendikep 2 mundësi',
      tr: '1. Yarı – Handikap 2 seçenekli',
      es: '1.ª parte – hándicap 2 opciones',
    },
  },
  {
    feed: ['1st Half Asian Handicap', 'Alternative 1st Half Asian Handicap'],
    labels: {
      en: '1st Half - Asian Handicap',
      de: '1. Halbzeit – Asiatisches Handicap',
      sr: '1. poluvreme – azijski hendikep',
      hr: '1. poluvrijeme – azijski hendikep',
      bs: '1. poluvrijeme – azijski hendikep',
      it: '1° tempo – handicap asiatico',
      fr: '1re mi-temps – handicap asiatique',
      sq: 'Pjesa e parë – hendikep aziatik',
      tr: '1. Yarı – Asya Handikapı',
      es: '1.ª parte – hándicap asiático',
    },
  },
  {
    feed: ['Goals Over/Under', 'Total Goals'],
    labels: {
      en: 'Over/Under',
      de: 'Über/Unter',
      sr: 'Više/manje golova',
      hr: 'Više/manje golova',
      bs: 'Više/manje golova',
      it: 'Under/Over gol',
      fr: 'Plus/Moins de buts',
      sq: 'Gola mbi/nën',
      tr: 'Alt/Üst',
      es: 'Más/Menos goles',
    },
  },
  {
    feed: ['Goal Line', 'Alternative Goal Line', 'Alternative Total Goals'],
    labels: {
      en: 'Over/Under',
      de: 'Über/Unter',
      sr: 'Više/manje golova',
      hr: 'Više/manje golova',
      bs: 'Više/manje golova',
      it: 'Under/Over gol',
      fr: 'Plus/Moins de buts',
      sq: 'Gola mbi/nën',
      tr: 'Alt/Üst',
      es: 'Más/Menos goles',
    },
  },
  {
    feed: ['Team Total Goals'],
    labels: {
      en: 'Over/Under Team',
      de: 'Über/Unter Team',
      sr: 'Više/manje golova tima',
      hr: 'Više/manje golova momčadi',
      bs: 'Više/manje golova tima',
      it: 'Under/Over gol squadra',
      fr: 'Plus/Moins de buts de l’équipe',
      sq: 'Gola mbi/nën të ekipit',
      tr: 'Takım Alt/Üst',
      es: 'Más/Menos goles del equipo',
    },
  },
  {
    feed: ['Draw No Bet'],
    labels: {
      en: 'Draw No Bet',
      de: 'Unentschieden, keine Wette',
      sr: 'Bez nerešenog',
      hr: 'Bez neriješenog',
      bs: 'Bez neriješenog',
      it: 'Draw no bet',
      fr: 'Remboursé si nul',
      sq: 'Barazimi pa bast',
      tr: 'Beraberlikte İade',
      es: 'Empate apuesta no válida',
    },
  },
  {
    feed: ['First Team to Score'],
    labels: {
      en: 'First Team to Score',
      de: 'Erstes Tor',
      sr: 'Prvi daje gol',
      hr: 'Prvi zabija gol',
      bs: 'Prvi daje gol',
      it: 'Prima squadra a segnare',
      fr: 'Première équipe à marquer',
      sq: 'Ekipi i parë që shënon',
      tr: 'İlk Golü Atan Takım',
      es: 'Primer equipo en marcar',
    },
  },
  {
    feed: ['Last Team to Score'],
    labels: {
      en: 'Who scores last?',
      de: 'Letztes Tor',
      sr: 'Poslednji daje gol',
      hr: 'Posljednji zabija gol',
      bs: 'Posljednji daje gol',
      it: 'Chi segna per ultimo?',
      fr: 'Qui marque en dernier ?',
      sq: 'Kush shënon i fundit?',
      tr: 'Son golü kim atar?',
      es: '¿Quién marca el último gol?',
    },
  },
  {
    feed: ['Both Teams to Score'],
    labels: {
      en: 'Both teams to score?',
      de: 'Beide Teams treffen?',
      sr: 'Oba tima daju gol?',
      hr: 'Obje momčadi zabijaju gol?',
      bs: 'Oba tima daju gol?',
      it: 'Entrambe le squadre segnano?',
      fr: 'Les deux équipes marquent ?',
      sq: 'Të dyja ekipet shënojnë?',
      tr: 'Karşılıklı gol?',
      es: '¿Ambos equipos marcan?',
    },
  },
  {
    feed: ['Half Time Result'],
    labels: {
      en: 'Who wins the 1.half?',
      de: 'Wer gewinnt die 1. Halbzeit?',
      sr: 'Ko osvaja 1. poluvreme?',
      hr: 'Tko osvaja 1. poluvrijeme?',
      bs: 'Ko osvaja 1. poluvrijeme?',
      it: 'Chi vince il 1° tempo?',
      fr: 'Qui gagne la 1re mi-temps ?',
      sq: 'Kush fiton pjesën e parë?',
      tr: 'İlk yarıyı kim kazanır?',
      es: '¿Quién gana la 1.ª parte?',
    },
  },
  {
    feed: ['2nd Half Result'],
    labels: {
      en: 'Who wins the 2nd half?',
      de: 'Wer gewinnt die 2. Halbzeit?',
      sr: 'Ko osvaja 2. poluvreme?',
      hr: 'Tko osvaja 2. poluvrijeme?',
      bs: 'Ko osvaja 2. poluvrijeme?',
      it: 'Chi vince il 2° tempo?',
      fr: 'Qui gagne la 2e mi-temps ?',
      sq: 'Kush fiton pjesën e dytë?',
      tr: 'İkinci yarıyı kim kazanır?',
      es: '¿Quién gana la 2.ª parte?',
    },
  },
  {
    feed: [
      '1st Half Goals',
      '1st Half Goal Line',
      'Alternative 1st Half Goal Line',
    ],
    labels: {
      en: '1st Half Totals',
      de: '1. Halbzeit – Über/Unter',
      sr: '1. poluvreme – više/manje',
      hr: '1. poluvrijeme – više/manje',
      bs: '1. poluvrijeme – više/manje',
      it: '1° tempo – under/over',
      fr: '1re mi-temps – plus/moins',
      sq: 'Pjesa e parë – mbi/nën',
      tr: '1. Yarı – Alt/Üst',
      es: '1.ª parte – más/menos',
    },
  },
  {
    feed: ['2nd Half Goals', '2nd Half Goal Line'],
    labels: {
      en: '2nd Half - Over/Under',
      de: '2. Halbzeit – Über/Unter',
      sr: '2. poluvreme – više/manje',
      hr: '2. poluvrijeme – više/manje',
      bs: '2. poluvrijeme – više/manje',
      it: '2° tempo – under/over',
      fr: '2e mi-temps – plus/moins',
      sq: 'Pjesa e dytë – mbi/nën',
      tr: '2. Yarı – Alt/Üst',
      es: '2.ª parte – más/menos',
    },
  },
  {
    feed: ['Both Teams to Score in 1st Half'],
    labels: {
      en: '1st Half - Both teams score a goal',
      de: '1. Halbzeit – beide Teams treffen',
      sr: '1. poluvreme – oba tima daju gol',
      hr: '1. poluvrijeme – obje momčadi zabijaju gol',
      bs: '1. poluvrijeme – oba tima daju gol',
      it: '1° tempo – entrambe segnano',
      fr: '1re mi-temps – les deux équipes marquent',
      sq: 'Pjesa e parë – të dyja shënojnë',
      tr: '1. Yarı – Karşılıklı Gol',
      es: '1.ª parte – ambos equipos marcan',
    },
  },
  {
    feed: ['Both Teams to Score in 2nd Half'],
    labels: {
      en: '2nd Half - Both teams score a goal',
      de: '2. Halbzeit – beide Teams treffen',
      sr: '2. poluvreme – oba tima daju gol',
      hr: '2. poluvrijeme – obje momčadi zabijaju gol',
      bs: '2. poluvrijeme – oba tima daju gol',
      it: '2° tempo – entrambe segnano',
      fr: '2e mi-temps – les deux équipes marquent',
      sq: 'Pjesa e dytë – të dyja shënojnë',
      tr: '2. Yarı – Karşılıklı Gol',
      es: '2.ª parte – ambos equipos marcan',
    },
  },
  {
    feed: ['Both Teams to Score 1st Half - 2nd Half'],
    labels: {
      en: 'Both teams to score - both halves',
      de: 'Beide Teams treffen – beide Halbzeiten',
      sr: 'Oba tima daju gol – oba poluvremena',
      hr: 'Obje momčadi zabijaju gol – oba poluvremena',
      bs: 'Oba tima daju gol – oba poluvremena',
      it: 'Entrambe segnano – in entrambi i tempi',
      fr: 'Les deux marquent – dans les deux mi-temps',
      sq: 'Të dyja shënojnë – në të dyja pjesët',
      tr: 'Karşılıklı Gol – her iki yarıda',
      es: 'Ambos marcan – en las dos partes',
    },
  },
  {
    feed: ['Clean Sheet'],
    labels: {
      en: 'Team receives no goal?',
      de: 'Team ohne Gegentor?',
      sr: 'Tim bez primljenog gola?',
      hr: 'Momčad bez primljenog gola?',
      bs: 'Tim bez primljenog gola?',
      it: 'Squadra senza gol subiti?',
      fr: 'Équipe sans but encaissé ?',
      sq: 'Ekipi pa gol të pësuar?',
      tr: 'Takım gol yemez mi?',
      es: '¿Equipo sin goles en contra?',
    },
  },
  {
    feed: ['To Score In Half'],
    labels: {
      en: 'Team to score in a half',
      de: 'Team trifft in einer Halbzeit',
      sr: 'Tim daje gol u poluvremenu',
      hr: 'Momčad zabija gol u poluvremenu',
      bs: 'Tim daje gol u poluvremenu',
      it: 'Squadra segna in un tempo',
      fr: 'Équipe marque dans une mi-temps',
      sq: 'Ekipi shënon në një pjesë',
      tr: 'Takım bir yarıda gol atar',
      es: 'Equipo marca en una parte',
    },
  },
  {
    feed: ['Home Team Exact Goals'],
    labels: {
      en: 'Goals Home Team',
      de: 'Tore Heimteam',
      sr: 'Golovi domaćina',
      hr: 'Golovi domaćina',
      bs: 'Golovi domaćina',
      it: 'Gol squadra di casa',
      fr: 'Buts de l’équipe à domicile',
      sq: 'Golat e vendasit',
      tr: 'Ev Sahibi Gol Sayısı',
      es: 'Goles equipo local',
    },
  },
  {
    feed: ['Away Team Exact Goals'],
    labels: {
      en: 'Goals Away Team',
      de: 'Tore Auswärtsteam',
      sr: 'Golovi gosta',
      hr: 'Golovi gosta',
      bs: 'Golovi gosta',
      it: 'Gol squadra ospite',
      fr: 'Buts de l’équipe à l’extérieur',
      sq: 'Golat e mysafirit',
      tr: 'Deplasman Gol Sayısı',
      es: 'Goles equipo visitante',
    },
  },
  {
    // "Under 2 / 2 or 3 / Over 3": a market of its own, not the exact
    // number; under one name the two read as the same bet twice (2026-11-04).
    feed: ['Number of Goals In Match'],
    labels: {
      en: 'Number of Goals in Match',
      de: 'Anzahl der Tore im Spiel',
      sr: 'Broj golova u meču',
      hr: 'Broj golova u utakmici',
      bs: 'Broj golova u meču',
      it: 'Numero di gol nella partita',
      fr: 'Nombre de buts dans le match',
      sq: 'Numri i golave në ndeshje',
      tr: 'Maçtaki Gol Sayısı',
      es: 'Número de goles en el partido',
    },
  },
  {
    feed: ['Exact Goals', 'Exact Total Goals'],
    labels: {
      en: 'Exact Number of Goals',
      de: 'Genaue Anzahl der Tore',
      sr: 'Tačan broj golova',
      hr: 'Točan broj golova',
      bs: 'Tačan broj golova',
      it: 'Numero esatto di gol',
      fr: 'Nombre exact de buts',
      sq: 'Numri i saktë i golave',
      tr: 'Kesin Gol Sayısı',
      es: 'Número exacto de goles',
    },
  },
  {
    feed: ['Goals Range', 'Match Goals Range'],
    labels: {
      en: 'Total Goals (aggregated)',
      de: 'Tore gesamt (Bereich)',
      sr: 'Ukupno golova (raspon)',
      hr: 'Ukupno golova (raspon)',
      bs: 'Ukupno golova (raspon)',
      it: 'Gol totali (fasce)',
      fr: 'Total de buts (tranches)',
      sq: 'Gola gjithsej (intervale)',
      tr: 'Toplam Gol (aralık)',
      es: 'Total de goles (rangos)',
    },
  },
  {
    feed: ['Team Goals Range'],
    labels: {
      en: 'Total Goals Team (aggregated)',
      de: 'Tore Team (Bereich)',
      sr: 'Golovi tima (raspon)',
      hr: 'Golovi momčadi (raspon)',
      bs: 'Golovi tima (raspon)',
      it: 'Gol squadra (fasce)',
      fr: 'Buts de l’équipe (tranches)',
      sq: 'Golat e ekipit (intervale)',
      tr: 'Takım Toplam Gol (aralık)',
      es: 'Goles del equipo (rangos)',
    },
  },
  {
    feed: ['1st Half Goals Range'],
    labels: {
      en: '1st Half - Total Goals (aggregated)',
      de: '1. Halbzeit – Tore gesamt (Bereich)',
      sr: '1. poluvreme – ukupno golova (raspon)',
      hr: '1. poluvrijeme – ukupno golova (raspon)',
      bs: '1. poluvrijeme – ukupno golova (raspon)',
      it: '1° tempo – gol totali (fasce)',
      fr: '1re mi-temps – total de buts (tranches)',
      sq: 'Pjesa e parë – gola gjithsej (intervale)',
      tr: '1. Yarı – Toplam Gol (aralık)',
      es: '1.ª parte – total de goles (rangos)',
    },
  },
  {
    feed: ['2nd Half Goals Range'],
    labels: {
      en: '2nd Half - Total Goals (aggregated)',
      de: '2. Halbzeit – Tore gesamt (Bereich)',
      sr: '2. poluvreme – ukupno golova (raspon)',
      hr: '2. poluvrijeme – ukupno golova (raspon)',
      bs: '2. poluvrijeme – ukupno golova (raspon)',
      it: '2° tempo – gol totali (fasce)',
      fr: '2e mi-temps – total de buts (tranches)',
      sq: 'Pjesa e dytë – gola gjithsej (intervale)',
      tr: '2. Yarı – Toplam Gol (aralık)',
      es: '2.ª parte – total de goles (rangos)',
    },
  },
  {
    feed: ['Half With Most Goals'],
    labels: {
      en: 'Highest Scoring Half',
      de: 'Torreichste Halbzeit',
      sr: 'Poluvreme sa više golova',
      hr: 'Poluvrijeme s više golova',
      bs: 'Poluvrijeme sa više golova',
      it: 'Tempo con più gol',
      fr: 'Mi-temps avec le plus de buts',
      sq: 'Pjesa me më shumë gola',
      tr: 'En Çok Gol Olan Yarı',
      es: 'Parte con más goles',
    },
  },
  {
    feed: ['Home Team Highest Scoring Half'],
    labels: {
      en: 'Highest Scoring Half - Home Team',
      de: 'Torreichste Halbzeit – Heimteam',
      sr: 'Poluvreme sa više golova – domaćin',
      hr: 'Poluvrijeme s više golova – domaćin',
      bs: 'Poluvrijeme sa više golova – domaćin',
      it: 'Tempo con più gol – casa',
      fr: 'Mi-temps avec le plus de buts – domicile',
      sq: 'Pjesa me më shumë gola – vendasi',
      tr: 'En Çok Gol Olan Yarı – Ev Sahibi',
      es: 'Parte con más goles – local',
    },
  },
  {
    feed: ['Away Team Highest Scoring Half'],
    labels: {
      en: 'Highest Scoring Half - Away Team',
      de: 'Torreichste Halbzeit – Auswärtsteam',
      sr: 'Poluvreme sa više golova – gost',
      hr: 'Poluvrijeme s više golova – gost',
      bs: 'Poluvrijeme sa više golova – gost',
      it: 'Tempo con più gol – ospite',
      fr: 'Mi-temps avec le plus de buts – extérieur',
      sq: 'Pjesa me më shumë gola – mysafiri',
      tr: 'En Çok Gol Olan Yarı – Deplasman',
      es: 'Parte con más goles – visitante',
    },
  },
  {
    feed: ['Goals Odd/Even'],
    labels: {
      en: 'Odd/Even Goals',
      de: 'Tore gerade/ungerade',
      sr: 'Golovi par/nepar',
      hr: 'Golovi par/nepar',
      bs: 'Golovi par/nepar',
      it: 'Gol pari/dispari',
      fr: 'Buts pair/impair',
      sq: 'Gola çift/tek',
      tr: 'Gol Tek/Çift',
      es: 'Goles par/impar',
    },
  },
  {
    feed: ['1st Half Goals Odd/Even'],
    labels: {
      en: '1st Half - Odd/Even',
      de: '1. Halbzeit – gerade/ungerade',
      sr: '1. poluvreme – par/nepar',
      hr: '1. poluvrijeme – par/nepar',
      bs: '1. poluvrijeme – par/nepar',
      it: '1° tempo – pari/dispari',
      fr: '1re mi-temps – pair/impair',
      sq: 'Pjesa e parë – çift/tek',
      tr: '1. Yarı – Tek/Çift',
      es: '1.ª parte – par/impar',
    },
  },
  {
    feed: ['2nd Half Goals Odd/Even'],
    labels: {
      en: '2nd Half - Odd/Even',
      de: '2. Halbzeit – gerade/ungerade',
      sr: '2. poluvreme – par/nepar',
      hr: '2. poluvrijeme – par/nepar',
      bs: '2. poluvrijeme – par/nepar',
      it: '2° tempo – pari/dispari',
      fr: '2e mi-temps – pair/impair',
      sq: 'Pjesa e dytë – çift/tek',
      tr: '2. Yarı – Tek/Çift',
      es: '2.ª parte – par/impar',
    },
  },
  {
    feed: ['Home Team Odd/Even Goals'],
    labels: {
      en: 'Odd/Even Goals Home Team',
      de: 'Tore gerade/ungerade Heimteam',
      sr: 'Golovi par/nepar – domaćin',
      hr: 'Golovi par/nepar – domaćin',
      bs: 'Golovi par/nepar – domaćin',
      it: 'Gol pari/dispari – casa',
      fr: 'Buts pair/impair – domicile',
      sq: 'Gola çift/tek – vendasi',
      tr: 'Ev Sahibi Gol Tek/Çift',
      es: 'Goles par/impar – local',
    },
  },
  {
    feed: ['Away Team Odd/Even Goals'],
    labels: {
      en: 'Odd/Even Goals Away Team',
      de: 'Tore gerade/ungerade Auswärtsteam',
      sr: 'Golovi par/nepar – gost',
      hr: 'Golovi par/nepar – gost',
      bs: 'Golovi par/nepar – gost',
      it: 'Gol pari/dispari – ospite',
      fr: 'Buts pair/impair – extérieur',
      sq: 'Gola çift/tek – mysafiri',
      tr: 'Deplasman Gol Tek/Çift',
      es: 'Goles par/impar – visitante',
    },
  },
  {
    feed: ['Half Time/Full Time'],
    labels: {
      en: 'Halftime/Fulltime',
      de: 'Halbzeit/Endstand',
      sr: 'Poluvreme/kraj',
      hr: 'Poluvrijeme/kraj',
      bs: 'Poluvrijeme/kraj',
      it: 'Parziale/finale',
      fr: 'Mi-temps/fin de match',
      sq: 'Pjesa e parë/fundi',
      tr: 'İlk Yarı/Maç Sonucu',
      es: 'Descanso/Final',
    },
  },
  // Goals combinations priced by Tottalo (tottalo_api computed_combos.go).
  {
    feed: ['Goals in Each Half'],
    labels: {
      en: 'Goals in 1st and 2nd half',
      de: 'Tore in 1. und 2. Halbzeit',
      sr: 'Broj golova u prvom i drugom poluvremenu',
      hr: 'Broj golova u prvom i drugom poluvremenu',
      bs: 'Broj golova u prvom i drugom poluvremenu',
      it: 'Gol nel 1° e 2° tempo',
      fr: 'Buts en 1re et 2e mi-temps',
      sq: 'Gola në pjesën e parë dhe të dytë',
      tr: '1. ve 2. yarı golleri',
      es: 'Goles en 1.ª y 2.ª parte',
    },
  },
  {
    feed: ['Result & Half Goals'],
    labels: {
      en: '3-way combinations',
      de: '1X2 Kombinationen',
      sr: 'Konačan ishod kombinacije',
      hr: 'Konačan ishod kombinacije',
      bs: 'Konačan ishod kombinacije',
      it: '1X2 combinazioni',
      fr: '1X2 combinaisons',
      sq: '1X2 kombinime',
      tr: 'MS kombinasyonları',
      es: '1X2 combinaciones',
    },
  },
  {
    feed: ['Result & Half With More Goals'],
    labels: {
      en: '3-way + half with more goals',
      de: '1X2 + Halbzeit mit mehr Toren',
      sr: 'Konačan ishod + više golova prvo ili drugo poluvreme',
      hr: 'Konačan ishod + više golova prvo ili drugo poluvrijeme',
      bs: 'Konačan ishod + više golova prvo ili drugo poluvrijeme',
      it: '1X2 + tempo con più gol',
      fr: '1X2 + mi-temps avec le plus de buts',
      sq: '1X2 + pjesa me më shumë gola',
      tr: 'MS + en çok gol olan yarı',
      es: '1X2 + parte con más goles',
    },
  },
  {
    feed: ['Double Chance & Half With More Goals'],
    labels: {
      en: 'Double chance + half with more goals',
      de: 'Doppelte Chance + Halbzeit mit mehr Toren',
      sr: 'Dupla šansa + više golova prvo ili drugo poluvreme',
      hr: 'Dupla šansa + više golova prvo ili drugo poluvrijeme',
      bs: 'Dupla šansa + više golova prvo ili drugo poluvrijeme',
      it: 'Doppia chance + tempo con più gol',
      fr: 'Double chance + mi-temps avec le plus de buts',
      sq: 'Shans i dyfishtë + pjesa me më shumë gola',
      tr: 'Çifte şans + en çok gol olan yarı',
      es: 'Doble oportunidad + parte con más goles',
    },
  },
  {
    feed: ['Double Chance & 1st Half Goals'],
    labels: {
      en: 'Double chance combinations',
      de: 'Doppelte Chance Kombinationen',
      sr: 'Dupla šansa kombinacije',
      hr: 'Dupla šansa kombinacije',
      bs: 'Dupla šansa kombinacije',
      it: 'Doppia chance combinazioni',
      fr: 'Double chance combinaisons',
      sq: 'Shans i dyfishtë kombinime',
      tr: 'Çifte şans kombinasyonları',
      es: 'Doble oportunidad combinaciones',
    },
  },
  {
    feed: ['Half Time/Full Time & Goals'],
    labels: {
      en: 'Halftime/Fulltime + goals',
      de: 'Halbzeit/Endstand + Tore',
      sr: 'Poluvreme/kraj + golovi',
      hr: 'Poluvrijeme/kraj + golovi',
      bs: 'Poluvrijeme/kraj + golovi',
      it: 'Parziale/finale + gol',
      fr: 'Mi-temps/fin + buts',
      sq: 'Pjesa e parë/fundi + gola',
      tr: 'İY/MS + goller',
      es: 'Descanso/Final + goles',
    },
  },
  {
    feed: ['Half Time/Full Time Combos'],
    labels: {
      en: 'Halftime/Fulltime combinations',
      de: 'Halbzeit/Endstand Kombinationen',
      sr: 'Poluvreme/kraj kombinacije',
      hr: 'Poluvrijeme/kraj kombinacije',
      bs: 'Poluvrijeme/kraj kombinacije',
      it: 'Parziale/finale combinazioni',
      fr: 'Mi-temps/fin combinaisons',
      sq: 'Pjesa e parë/fundi kombinime',
      tr: 'İY/MS kombinasyonları',
      es: 'Descanso/Final combinaciones',
    },
  },
  {
    // Live, priced by Tottalo (tottalo_api computed_rest_btts.go).
    feed: ['Rest of Match - Both Teams to Score'],
    labels: {
      en: 'Both teams to score – rest of match',
      de: 'Beide treffen – Restspiel',
      sr: 'Gol-gol ostatak meča',
      hr: 'Gol-gol ostatak utakmice',
      bs: 'Gol-gol ostatak meča',
      it: 'Gol/Gol – resto della partita',
      fr: 'Les deux marquent – reste du match',
      sq: 'Të dyja shënojnë – pjesa e mbetur',
      tr: 'KG Var – maçın kalanı',
      es: 'Ambos marcan – resto del partido',
    },
  },
  {
    // Priced by Tottalo from bet365's markets (tottalo_api computed_htft.go).
    feed: ['Half Time/Full Time & 3+ Goals'],
    labels: {
      en: 'Halftime/Fulltime + over 2.5 goals',
      de: 'Halbzeit/Endstand + über 2.5 Tore',
      sr: 'Poluvreme/kraj + preko 2.5 gola',
      hr: 'Poluvrijeme/kraj + više 2.5 gola',
      bs: 'Poluvrijeme/kraj + više 2.5 gola',
      it: 'Parziale/finale + over 2.5 gol',
      fr: 'Mi-temps/fin + plus de 2.5 buts',
      sq: 'Pjesa e parë/fundi + mbi 2.5 gola',
      tr: 'İY/MS + 2.5 üst gol',
      es: 'Descanso/Final + más de 2.5 goles',
    },
  },
  {
    feed: ['Half Time Correct Score'],
    labels: {
      en: '1st Half - Correct Score',
      de: '1. Halbzeit – genaues Ergebnis',
      sr: '1. poluvreme – tačan rezultat',
      hr: '1. poluvrijeme – točan rezultat',
      bs: '1. poluvrijeme – tačan rezultat',
      it: '1° tempo – risultato esatto',
      fr: '1re mi-temps – score exact',
      sq: 'Pjesa e parë – rezultati i saktë',
      tr: '1. Yarı – Kesin Skor',
      es: '1.ª parte – resultado exacto',
    },
  },
  {
    feed: ['Correct Score'],
    labels: {
      en: 'Correct score',
      de: 'Genaues Ergebnis',
      sr: 'Tačan rezultat',
      hr: 'Točan rezultat',
      bs: 'Tačan rezultat',
      it: 'Risultato esatto',
      fr: 'Score exact',
      sq: 'Rezultati i saktë',
      tr: 'Kesin Skor',
      es: 'Resultado exacto',
    },
  },
  {
    feed: ['Goalscorers'],
    labels: {
      en: 'Goalscorers',
      de: 'Torschützen',
      sr: 'Strelci',
      hr: 'Strijelci',
      bs: 'Strijelci',
      it: 'Marcatori',
      fr: 'Buteurs',
      sq: 'Golashënuesit',
      tr: 'Gol Atanlar',
      es: 'Goleadores',
    },
  },
  {
    feed: ['Team Goalscorer'],
    labels: {
      en: 'Team Goalscorer',
      de: 'Torschütze des Teams',
      sr: 'Strelac tima',
      hr: 'Strijelac momčadi',
      bs: 'Strijelac tima',
      it: 'Marcatore della squadra',
      fr: 'Buteur de l’équipe',
      sq: 'Golashënuesi i ekipit',
      tr: 'Takımın Golcüsü',
      es: 'Goleador del equipo',
    },
  },
  {
    feed: ['Multi Scorers'],
    labels: {
      en: 'Multi Scorers',
      de: 'Mehrfach-Torschützen',
      sr: 'Višestruki strelci',
      hr: 'Višestruki strijelci',
      bs: 'Višestruki strijelci',
      it: 'Marcatori multipli',
      fr: 'Buteurs multiples',
      sq: 'Golashënues të shumëfishtë',
      tr: 'Çoklu Gol Atanlar',
      es: 'Goleadores múltiples',
    },
  },
  {
    feed: ['Score or Assist'],
    labels: {
      en: 'Score or Assist',
      de: 'Tor oder Vorlage',
      sr: 'Gol ili asistencija',
      hr: 'Gol ili asistencija',
      bs: 'Gol ili asistencija',
      it: 'Gol o assist',
      fr: 'But ou passe décisive',
      sq: 'Gol ose asist',
      tr: 'Gol veya Asist',
      es: 'Gol o asistencia',
    },
  },
  {
    feed: ['Result / Total Goals'],
    labels: {
      en: '3-way + Totals',
      de: '1X2 + Über/Unter',
      sr: 'Konačan ishod + golovi',
      hr: 'Konačan ishod + golovi',
      bs: 'Konačan ishod + golovi',
      it: '1X2 + under/over',
      fr: '1X2 + plus/moins',
      sq: '1X2 + mbi/nën',
      tr: '1X2 + Alt/Üst',
      es: '1X2 + más/menos',
    },
  },
  {
    // Tottalo's lines of bet365's "Result / Total Goals" (the same bet):
    // "more lines", as the other line families (2026-11-04).
    feed: ['Result & Goals'],
    labels: {
      en: '3-way + goals – more lines',
      de: '1X2 + Tore – weitere Linien',
      sr: 'Konačan ishod + golovi – više linija',
      hr: 'Konačan ishod + golovi – više linija',
      bs: 'Konačan ishod + golovi – više linija',
      it: '1X2 + gol – altre linee',
      fr: '1X2 + buts – plus de lignes',
      sq: '1X2 + gola – më shumë linja',
      tr: 'MS + gol – daha fazla çizgi',
      es: '1X2 + goles – más líneas',
    },
  },
  {
    feed: ['Total Goals/Both Teams to Score'],
    labels: {
      en: 'Both teams to score + Over/Under',
      de: 'Beide Teams treffen + Über/Unter',
      sr: 'Oba tima daju gol + više/manje',
      hr: 'Obje momčadi zabijaju gol + više/manje',
      bs: 'Oba tima daju gol + više/manje',
      it: 'Entrambe segnano + under/over',
      fr: 'Les deux marquent + plus/moins',
      sq: 'Të dyja shënojnë + mbi/nën',
      tr: 'Karşılıklı Gol + Alt/Üst',
      es: 'Ambos marcan + más/menos',
    },
  },
  {
    feed: ['Result/Both Teams to Score'],
    labels: {
      en: '3-way + Both Teams to Score',
      de: '1X2 + beide Teams treffen',
      sr: '1X2 + oba tima daju gol',
      hr: '1X2 + obje momčadi zabijaju gol',
      bs: '1X2 + oba tima daju gol',
      it: '1X2 + entrambe segnano',
      fr: '1X2 + les deux marquent',
      sq: '1X2 + të dyja shënojnë',
      tr: '1X2 + Karşılıklı Gol',
      es: '1X2 + ambos marcan',
    },
  },
  {
    feed: ['Half Time Result / Total Goals'],
    labels: {
      en: '1st Half - 3-way + Over/Under',
      de: '1. Halbzeit – 1X2 + Über/Unter',
      sr: '1. poluvreme – ishod + golovi',
      hr: '1. poluvrijeme – ishod + golovi',
      bs: '1. poluvrijeme – ishod + golovi',
      it: '1° tempo – 1X2 + under/over',
      fr: '1re mi-temps – 1X2 + plus/moins',
      sq: 'Pjesa e parë – 1X2 + mbi/nën',
      tr: '1. Yarı – 1X2 + Alt/Üst',
      es: '1.ª parte – 1X2 + más/menos',
    },
  },
  {
    feed: ['Half Time Result/Both Teams To Score'],
    labels: {
      en: '1st Half - 3-way + Both teams to score',
      de: '1. Halbzeit – 1X2 + beide Teams treffen',
      sr: '1. poluvreme – 1X2 + oba tima daju gol',
      hr: '1. poluvrijeme – 1X2 + obje momčadi zabijaju gol',
      bs: '1. poluvrijeme – 1X2 + oba tima daju gol',
      it: '1° tempo – 1X2 + entrambe segnano',
      fr: '1re mi-temps – 1X2 + les deux marquent',
      sq: 'Pjesa e parë – 1X2 + të dyja shënojnë',
      tr: '1. Yarı – 1X2 + Karşılıklı Gol',
      es: '1.ª parte – 1X2 + ambos marcan',
    },
  },
  {
    feed: ['Half Time Double Chance'],
    labels: {
      en: '1st Half - Double Chance',
      de: '1. Halbzeit – Doppelte Chance',
      sr: '1. poluvreme – dvostruka šansa',
      hr: '1. poluvrijeme – dvostruka šansa',
      bs: '1. poluvrijeme – dvostruka šansa',
      it: '1° tempo – doppia chance',
      fr: '1re mi-temps – double chance',
      sq: 'Pjesa e parë – shans i dyfishtë',
      tr: '1. Yarı – Çifte Şans',
      es: '1.ª parte – doble oportunidad',
    },
  },
  {
    feed: ['Double Chance/Goals Range'],
    labels: {
      en: 'Double chance + Total Goals (aggregated)',
      de: 'Doppelte Chance + Tore gesamt (Bereich)',
      sr: 'Dupla šansa + golovi (raspon)',
      hr: 'Dupla šansa + golovi (raspon)',
      bs: 'Dupla šansa + golovi (raspon)',
      it: 'Doppia chance + gol totali (fasce)',
      fr: 'Double chance + total de buts (tranches)',
      sq: 'Shans i dyfishtë + gola gjithsej (intervale)',
      tr: 'Çifte Şans + Toplam Gol (aralık)',
      es: 'Doble oportunidad + total de goles (rangos)',
    },
  },
  {
    feed: ['Results/Goals Range'],
    labels: {
      en: '3-way + Total Goals (aggregated)',
      de: '1X2 + Tore gesamt (Bereich)',
      sr: 'Konačan ishod + golovi (raspon)',
      hr: 'Konačan ishod + golovi (raspon)',
      bs: 'Konačan ishod + golovi (raspon)',
      it: '1X2 + gol totali (fasce)',
      fr: '1X2 + total de buts (tranches)',
      sq: '1X2 + gola gjithsej (intervale)',
      tr: '1X2 + Toplam Gol (aralık)',
      es: '1X2 + total de goles (rangos)',
    },
  },
  {
    feed: ['Teams to Score'],
    labels: {
      en: 'Which teams score?',
      de: 'Welche Teams treffen?',
      sr: 'Koji timovi daju gol?',
      hr: 'Koje momčadi zabijaju gol?',
      bs: 'Koji timovi daju gol?',
      it: 'Quali squadre segnano?',
      fr: 'Quelles équipes marquent ?',
      sq: 'Cilat ekipe shënojnë?',
      tr: 'Hangi takımlar gol atar?',
      es: '¿Qué equipos marcan?',
    },
  },
  {
    feed: ['Winning Margin'],
    labels: {
      en: 'Winning Margin',
      de: 'Siegdifferenz',
      sr: 'Razlika u pobedi',
      hr: 'Razlika u pobjedi',
      bs: 'Razlika u pobjedi',
      it: 'Margine di vittoria',
      fr: 'Écart de victoire',
      sq: 'Diferenca e fitores',
      tr: 'Galibiyet Farkı',
      es: 'Margen de victoria',
    },
  },
  {
    // bet365's "Goal Method" is the match's first goal (its description
    // says so); the name says it too (owner, 2026-10-31).
    // "Goal Method" names a player with the way ("Header - Barry"), "First
    // Goal Method" only the way: one name for both read as the same bet
    // twice (2026-11-04).
    feed: ['Goal Method'],
    labels: {
      en: '1st Goal Method - Player',
      de: 'Art des 1. Tores - Spieler',
      sr: 'Način postizanja 1. gola – igrač',
      hr: 'Način postizanja 1. gola – igrač',
      bs: 'Način postizanja 1. gola – igrač',
      it: 'Modalità del 1° gol - giocatore',
      fr: 'Façon de marquer le 1er but - joueur',
      sq: 'Mënyra e golit të parë - lojtari',
      tr: '1. Golün Atılış Şekli - Oyuncu',
      es: 'Forma del 1.er gol - jugador',
    },
  },
  {
    feed: ['First Goal Method'],
    labels: {
      en: '1st Goal Method',
      de: 'Art des 1. Tores',
      sr: 'Način postizanja 1. gola',
      hr: 'Način postizanja 1. gola',
      bs: 'Način postizanja 1. gola',
      it: 'Modalità del 1° gol',
      fr: 'Façon de marquer le 1er but',
      sq: 'Mënyra e golit të parë',
      tr: '1. Golün Atılış Şekli',
      es: 'Forma del 1.er gol',
    },
  },
  {
    feed: ['Own Goal'],
    labels: {
      en: 'Own Goal',
      de: 'Eigentor',
      sr: 'Autogol',
      hr: 'Autogol',
      bs: 'Autogol',
      it: 'Autogol',
      fr: 'But contre son camp',
      sq: 'Autogol',
      tr: 'Kendi Kalesine Gol',
      es: 'Gol en propia puerta',
    },
  },
  {
    feed: ['To Score a Penalty'],
    labels: {
      en: 'Penalty scored',
      de: 'Elfmeter verwandelt',
      sr: 'Realizovan penal',
      hr: 'Realiziran jedanaesterac',
      bs: 'Realizovan penal',
      it: 'Rigore segnato',
      fr: 'Penalty marqué',
      sq: 'Penallti e shënuar',
      tr: 'Penaltı Golü',
      es: 'Penalti marcado',
    },
  },
  {
    feed: ['To Miss a Penalty'],
    labels: {
      en: 'Penalty missed',
      de: 'Elfmeter verschossen',
      sr: 'Promašen penal',
      hr: 'Promašen jedanaesterac',
      bs: 'Promašen penal',
      it: 'Rigore sbagliato',
      fr: 'Penalty manqué',
      sq: 'Penallti e humbur',
      tr: 'Kaçan Penaltı',
      es: 'Penalti fallado',
    },
  },
  {
    feed: ['Time of First Goal Brackets'],
    labels: {
      en: 'Time of First Goal',
      de: 'Zeitpunkt des ersten Tores',
      sr: 'Vreme prvog gola',
      hr: 'Vrijeme prvog gola',
      bs: 'Vrijeme prvog gola',
      it: 'Minuto del primo gol',
      fr: 'Minute du premier but',
      sq: 'Koha e golit të parë',
      tr: 'İlk Golün Dakikası',
      es: 'Minuto del primer gol',
    },
  },
  {
    feed: ['Time of 1st Team Goal'],
    labels: {
      en: "Time of Team's First Goal",
      de: 'Zeitpunkt des ersten Team-Tores',
      sr: 'Vreme prvog gola tima',
      hr: 'Vrijeme prvog gola momčadi',
      bs: 'Vrijeme prvog gola tima',
      it: 'Minuto del primo gol della squadra',
      fr: 'Minute du premier but de l’équipe',
      sq: 'Koha e golit të parë të ekipit',
      tr: 'Takımın İlk Golünün Dakikası',
      es: 'Minuto del primer gol del equipo',
    },
  },
  {
    feed: ['Early Goal'],
    labels: {
      en: 'Early Goal',
      de: 'Frühes Tor',
      sr: 'Rani gol',
      hr: 'Rani gol',
      bs: 'Rani gol',
      it: 'Gol a inizio partita',
      fr: 'But rapide',
      sq: 'Gol i hershëm',
      tr: 'Erken Gol',
      es: 'Gol temprano',
    },
  },
  {
    feed: ['Late Goal'],
    labels: {
      en: 'Late Goal',
      de: 'Spätes Tor',
      sr: 'Kasni gol',
      hr: 'Kasni gol',
      bs: 'Kasni gol',
      it: 'Gol nel finale',
      fr: 'But tardif',
      sq: 'Gol në fund',
      tr: 'Geç Gol',
      es: 'Gol tardío',
    },
  },
  {
    feed: ['Total Goal Minutes'],
    labels: {
      en: 'Total Goal Minutes',
      de: 'Summe der Torminuten',
      sr: 'Zbir minuta golova',
      hr: 'Zbroj minuta golova',
      bs: 'Zbir minuta golova',
      it: 'Somma dei minuti dei gol',
      fr: 'Total des minutes des buts',
      sq: 'Shuma e minutave të golave',
      tr: 'Gol Dakikaları Toplamı',
      es: 'Suma de los minutos de gol',
    },
  },
  {
    feed: ['First 10 Minutes'],
    labels: {
      en: 'First 10 Minutes',
      de: 'Erste 10 Minuten',
      sr: 'Prvih 10 minuta',
      hr: 'Prvih 10 minuta',
      bs: 'Prvih 10 minuta',
      it: 'Primi 10 minuti',
      fr: '10 premières minutes',
      sq: '10 minutat e para',
      tr: 'İlk 10 Dakika',
      es: 'Primeros 10 minutos',
    },
  },
  {
    feed: ['10 Minute Result'],
    labels: {
      en: '10 Minute Result',
      de: 'Ergebnis nach 10 Minuten',
      sr: 'Rezultat posle 10 minuta',
      hr: 'Rezultat poslije 10 minuta',
      bs: 'Rezultat poslije 10 minuta',
      it: 'Esito dopo 10 minuti',
      fr: 'Résultat après 10 minutes',
      sq: 'Rezultati pas 10 minutash',
      tr: '10 Dakika Sonucu',
      es: 'Resultado a los 10 minutos',
    },
  },
  {
    feed: ['Corners', 'Alternative Corners'],
    labels: {
      en: 'Corners',
      de: 'Eckbälle',
      sr: 'Korneri',
      hr: 'Korneri',
      bs: 'Korneri',
      it: 'Calci d’angolo',
      fr: 'Corners',
      sq: 'Kënde',
      tr: 'Kornerler',
      es: 'Córners',
    },
  },
  {
    feed: ['Asian Corners', 'Asian Total Corners'],
    labels: {
      en: 'Asian Corners',
      de: 'Asiatische Eckbälle',
      sr: 'Azijski korneri',
      hr: 'Azijski korneri',
      bs: 'Azijski korneri',
      it: 'Angoli asiatici',
      fr: 'Corners asiatiques',
      sq: 'Kënde aziatike',
      tr: 'Asya Kornerleri',
      es: 'Córners asiáticos',
    },
  },
  {
    feed: ['1st Half Corners'],
    labels: {
      en: '1st Half - Corners',
      de: '1. Halbzeit – Eckbälle',
      sr: '1. poluvreme – korneri',
      hr: '1. poluvrijeme – korneri',
      bs: '1. poluvrijeme – korneri',
      it: '1° tempo – calci d’angolo',
      fr: '1re mi-temps – corners',
      sq: 'Pjesa e parë – kënde',
      tr: '1. Yarı – Kornerler',
      es: '1.ª parte – córners',
    },
  },
  {
    feed: ['1st Half Asian Corners'],
    labels: {
      en: '1st Half - Asian Corners',
      de: '1. Halbzeit – Asiatische Eckbälle',
      sr: '1. poluvreme – azijski korneri',
      hr: '1. poluvrijeme – azijski korneri',
      bs: '1. poluvrijeme – azijski korneri',
      it: '1° tempo – angoli asiatici',
      fr: '1re mi-temps – corners asiatiques',
      sq: 'Pjesa e parë – kënde aziatike',
      tr: '1. Yarı – Asya Kornerleri',
      es: '1.ª parte – córners asiáticos',
    },
  },
];

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Feed name (normalised) → its place in the list and its names. */
const BY_FEED_NAME = new Map<string, { order: number; labels: Labels }>();
CATALOG.forEach((entry, order) => {
  entry.feed.forEach((feedName) => {
    const key = normalize(feedName);
    if (!BY_FEED_NAME.has(key))
      BY_FEED_NAME.set(key, { order, labels: entry.labels });
  });
});

/** Markets the catalogue does not cover sort after every one it does. */
export const UNLISTED_MARKET_ORDER = CATALOG.length + 1;

const langOf = (raw?: string): keyof Labels => appLanguageOf(raw);

/**
 * What this market is called on screen, or null when the catalogue has no entry
 * for it and the caller should fall back to the translated feed name.
 */
export function marketDisplayLabel(
  feedName?: string | null,
  language?: string
): string | null {
  if (!feedName) return null;
  const hit = BY_FEED_NAME.get(normalize(feedName));
  if (!hit) return null;
  return hit.labels[langOf(language ?? i18next.language)];
}

/** Where this market belongs in the list. */
export function marketOrderIndex(feedName?: string | null): number {
  if (!feedName) return UNLISTED_MARKET_ORDER;
  return BY_FEED_NAME.get(normalize(feedName))?.order ?? UNLISTED_MARKET_ORDER;
}

/**
 * Put markets in the catalogue's order, so a league reads the same way every
 * time it is opened. Markets the catalogue does not know keep the feed's own
 * priority order behind the ones it does.
 */
export function sortMarketsForDisplay<
  T extends { name?: string | null; priority?: number | null },
>(bets: T[]): T[] {
  return bets
    .map((bet, index) => ({ bet, index }))
    .sort((a, b) => {
      const ao = marketOrderIndex(a.bet?.name);
      const bo = marketOrderIndex(b.bet?.name);
      if (ao !== bo) return ao - bo;
      const ap = a.bet?.priority ?? Number.MAX_SAFE_INTEGER;
      const bp = b.bet?.priority ?? Number.MAX_SAFE_INTEGER;
      if (ap !== bp) return ap - bp;
      // Same place and same priority: keep the order they arrived in.
      return a.index - b.index;
    })
    .map(({ bet }) => bet);
}

/** For tests: every feed name the catalogue covers. */
export const CATALOG_FEED_NAMES = CATALOG.flatMap((entry) => entry.feed);
